/**
 * PlayerList Component
 *
 * Displays list of players in the lobby. Shows player info,
 * ready status, and player colors with click handlers.
 *
 * Used by:
 * - LobbyPhase
 *
 * Responsibilities:
 * - Display player cards with profile info
 * - Show ready/not ready status
 * - Display player colors (clickable)
 * - Handle color selection click (opens ColorPicker)
 *
 * Dependencies: types/game.types.ts, utils/colors.ts, utils/images.ts
 */

import React from 'react';
import { isViteDevelopmentMode } from '../../../config/env';
import { COLORS } from '../../../utils/colors';
import { IMAGES } from '../../../utils/images';
import type { RoomPlayer } from '../../types/game.types';

/**
 * Player color type - matches colors.ts definition
 */
type PlayerColor =
    | 'red'
    | 'orange'
    | 'yellow'
    | 'green'
    | 'cyan'
    | 'blue'
    | 'dark_blue'
    | 'black'
    | 'purple';

export interface PlayerListProps {
    /** Array of players in the lobby */
    players: RoomPlayer[];
    /** Current user's ID – number for logged-in user, string (guestId) for guest */
    currentUserId: number | string | null;
    /** Current username (to check if can change color) */
    currentUsername: string;
    /** Color of player A */
    playerAColor: PlayerColor;
    /** Color of player B */
    playerBColor: PlayerColor;
    /** Callback when player color is clicked */
    onColorClick: () => void;
}

/**
 * PlayerList Component
 */
export const PlayerList: React.FC<PlayerListProps> = ({
    players,
    currentUserId,
    currentUsername,
    playerAColor,
    playerBColor,
    onColorClick,
}) => {
    /**
     * Get player color based on player letter
     */
    const getPlayerColor = (playerLetter: 'A' | 'B'): PlayerColor => {
        return playerLetter === 'A' ? playerAColor : playerBColor;
    };

    /**
     * Check if current user can change this player's color
     */
    const canChangeColor = (player: RoomPlayer): boolean => {
        const isCurrentUser =
            (player.userId !== null && player.userId === currentUserId) ||
            (player.guestId !== null && player.guestId === currentUserId);
        return isCurrentUser && player.username === currentUsername;
    };

    return (
        <div className="room_players_container">
            <p className="section_title">Players</p>
            {players.map((player) => {
                const ready = player.ready;
                const playerColor = getPlayerColor(player.id);

                return (
                    <div key={player.socketId || player.id} className="player_container">
                        <div className="player_left">
                            <div className="player_left_top">
                                <div className="player_pic_container">
                                    <img
                                        src={IMAGES.profile_pics['default.png']}
                                        className="profile_pic"
                                        alt={`${player.username} profile`}
                                    />
                                </div>
                                <div className="player_text_container">
                                    <div className="player_name">
                                        {isViteDevelopmentMode ? `${player.id}: ${player.username}` : player.username}
                                    </div>
                                    <div className="player_title">
                                        {player.title || 'Cadet'}
                                    </div>
                                </div>
                            </div>
                            <div className="lobby_ready_text">
                                {ready ? 'Ready' : 'Not Ready'}
                            </div>
                        </div>
                        <div className="player_right">
                            <div
                                className="player_color"
                                style={{
                                    backgroundColor: playerColor
                                        ? COLORS.player_colors[playerColor]
                                        : COLORS.player_colors.red,
                                    cursor: canChangeColor(player) ? 'pointer' : 'default',
                                }}
                                onClick={() => {
                                    if (canChangeColor(player)) {
                                        onColorClick();
                                    }
                                }}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default PlayerList;
